/**
 * ZenythADK Dialog Component
 * Implements accessible dialogs according to WAI-ARIA guidelines
 * Based on: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/dialog/
 */

class ZntDialog {
  constructor() {
    this.initialized = false;
    this.activeTrigger = null;
    this.init();
  }

  init() {
    if (this.initialized) return;
    
    this.moveDialogsToBodyEnd();
    this.setupTriggers();
    this.initialized = true;
  }
  
  moveDialogsToBodyEnd() {
    const backdrops = document.querySelectorAll('.znt-dialog_backdrop');
    const containers = document.querySelectorAll('.znt-dialog_container');
    
    backdrops.forEach(backdrop => {
      if (!document.body.contains(backdrop)) {
        document.body.appendChild(backdrop);
      }
    });
    
    containers.forEach(container => {
      if (!document.body.contains(container)) {
        document.body.appendChild(container);
      }
    });
  }
  
  setupTriggers() {
    const triggers = document.querySelectorAll('.znt-dialog_trigger');
    
    triggers.forEach(trigger => {
      if (trigger.hasAttribute('data-znt-dialog-initialized')) return;
      
      const sectionId = trigger.getAttribute('data-section');
      const backdrop = document.querySelector(`.znt-dialog_backdrop[data-section="${sectionId}"]`);
      const container = document.querySelector(`.znt-dialog_container[data-section="${sectionId}"]`);
      const closeBtn = container ? container.querySelector('.znt-dialog_close') : null;
      
      if (!backdrop || !container) return;
      
      const dialogData = {
        backdrop,
        container,
        closeBtn,
        trigger: trigger
      };
      
      trigger.setAttribute('data-znt-dialog-initialized', 'true');
      
      const openDialogHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.activeTrigger = trigger;
        this.openDialog(trigger, dialogData);
      };
      
      trigger.addEventListener('click', openDialogHandler);
      
      if (closeBtn) {
        const closeDialogHandler = () => {
          this.closeDialog(trigger, dialogData);
        };
        closeBtn.addEventListener('click', closeDialogHandler);
      }
      
      const backdropClickHandler = () => {
        this.closeDialog(trigger, dialogData);
      };
      backdrop.addEventListener('click', backdropClickHandler);
      
      const containerClickHandler = (e) => {
        e.stopPropagation();
      };
      container.addEventListener('click', containerClickHandler);
    });
    
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        const openContainer = document.querySelector('.znt-dialog_container.znt--open');
        if (openContainer && this.activeTrigger) {
          // Immediately focus the specific trigger that opened this dialog
          this.activeTrigger.focus();
          
          const dialogData = {
            backdrop: document.querySelector(`.znt-dialog_backdrop[data-section="${this.activeTrigger.getAttribute('data-section')}"]`),
            container: openContainer,
            trigger: this.activeTrigger
          };
          this.closeDialog(this.activeTrigger, dialogData);
        }
      }
    };
    
    document.removeEventListener('keydown', this._escHandler);
    document.addEventListener('keydown', escHandler);
    this._escHandler = escHandler;
  }
  
  openDialog(trigger, dialogData) {
    const { backdrop, container } = dialogData;
    
    if (!backdrop || !container) return;
    
    backdrop.classList.add('znt--open');
    container.classList.add('znt--open');
    document.body.classList.add('znt-dialog-open');
    
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length) {
      focusableElements[0].focus();
    }
    
    const trapFocusHandler = this.trapFocus.bind(this, container);
    container.removeEventListener('keydown', container._trapFocusHandler);
    container.addEventListener('keydown', trapFocusHandler);
    container._trapFocusHandler = trapFocusHandler;
  }
  
  closeDialog(trigger, dialogData) {
    const { backdrop, container } = dialogData;
    
    if (!backdrop || !container) return;
    
    backdrop.classList.remove('znt--open');
    container.classList.remove('znt--open');
    document.body.classList.remove('znt-dialog-open');
    
    if (this.activeTrigger) {
      this.activeTrigger.focus();
      this.activeTrigger = null;
    }
    
    container.removeEventListener('keydown', container._trapFocusHandler);
  }
  
  trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  }
}

try {
  window.zntDialog = new ZntDialog();
} catch (e) {
  console.error('Error initializing ZntDialog:', e);
} 